import React, { ComponentType, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch, useAppSelector } from '@/App/hooks';
import { selectAppConfig } from '@/features/config/selectAppConfig';
import { selectLocale } from '@/features/i18n/selectLocale';
import { getAppInfoThunk, resetInfo } from '@/features/info/infoSlice';
import { selectAppInfoInitialized } from '@/features/info/selectAppInfoInitialized';
import { selectTimeoutConfig } from '@/features/info/selectTimeoutConfig';
import { getCurrentDateFormatted } from '@/helpers/date/getCurrentDateFormatted';
import { getMillisUntilStartOfNext } from '@/helpers/date/getMillisUntilStartOfNext';

export const withInfo = <T extends object>(WrappedComponent: ComponentType<T>) => {
  const HOC = (props: T) => {
    const dispatch = useAppDispatch();
    const initialized = useSelector(selectAppInfoInitialized);
    const timeoutConfig = useSelector(selectTimeoutConfig);
    const { dateFormat: serverFormat } = useAppSelector(selectAppConfig);
    const locale = useSelector(selectLocale);

    useEffect(() => {
      if (!initialized) {
        const input = {
          date: getCurrentDateFormatted(serverFormat),
          locale,
        };
        dispatch(getAppInfoThunk(input));
      }
    }, [dispatch, initialized, serverFormat, locale]);

    useEffect(() => {
      if (initialized) {
        const { value, unit } = timeoutConfig;
        const millis = getMillisUntilStartOfNext(value, unit);

        // Set up timeout to reset info at the start of the next day
        const intervalId = setTimeout(() => {
          dispatch(resetInfo());
        }, millis);

        return () => clearTimeout(intervalId);
      }
    }, [dispatch, initialized, timeoutConfig]);

    if (!initialized) {
      return null;
    }

    return <WrappedComponent {...props} />;
  };
  HOC.displayName = `withInfo(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
  return HOC;
};
